import React from 'react';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Checkbox, FormHelperText, InputLabel, MenuItem, SxProps } from '@mui/material';

interface BkSelectProps {
  label?: string;
  data: string[] | undefined | { id: string; label: string }[];
  onChange: (event: SelectChangeEvent<any>) => void;
  value: string | string[] | undefined;
  error: string | undefined;
  disabled: boolean;
  placeholder?: string;
  width?: string;
  customStyles?: SxProps;
  hasLabel?: boolean;
  name?: string;
  hideHelperText?: boolean;
  multiple?: boolean;
  autoComplete?: string;
}

function isDataArrayOfObjects(arr: any): arr is { id: string; label: string }[] {
  return (
    Array.isArray(arr) &&
    arr.every(
      (item) =>
        typeof item === 'object' && item !== null && (typeof item.id === 'string' || typeof item.id === 'number') && typeof item.label === 'string',
    )
  );
}

//default values so that the select options always open under the select bar
const ITEM_HEIGHT = 48;

const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const BkSelect: React.FC<BkSelectProps> = ({
  label,
  data,
  onChange,
  value,
  error,
  disabled,
  placeholder,
  width,
  customStyles,
  hasLabel = true,
  name,
  hideHelperText,
  multiple,
  autoComplete
}) => {
  return (
    <FormControl sx={{ width: width !== undefined ? width : '100%' }}>
      {hasLabel && (
        <InputLabel error={error !== '' && error !== undefined} shrink={true} id={`bkselect-${label}`}>
          {label}
        </InputLabel>
      )}
      <Select
        label={label}
        labelId={`bkselect-${label}`}
        notched={true}
        autoComplete={autoComplete}
        value={value}
        error={error !== '' && error !== undefined}
        sx={{
          '& .MuiSelect-select': {
            padding: '0.625rem',
          },
          ...customStyles,
        }}
        MenuProps={MenuProps}
        name={name}
        onChange={onChange}
        multiple={multiple}
        disabled={disabled}
        placeholder={placeholder}
        displayEmpty
        renderValue={(value: any) => {
          if (!value || value.length === 0) {
            return placeholder;
          }
          if (!isDataArrayOfObjects(data)) {
            return multiple ? value.join(', ') : value;
          }
          const getLabel = (id: string) => data.find((item) => item.id === id)?.label || id;
          return multiple ? value.map(getLabel).join(', ') : getLabel(value);
        }}
      >
        {data !== undefined ? (
          data.map((item, index) => {
            if (typeof item === 'string') {
              return (
                <MenuItem key={index} value={item}>
                  {multiple && <Checkbox checked={value?.includes(item)} />}
                  {item}
                </MenuItem>
              );
            } else if (item !== undefined && item !== null) {
              return (
                <MenuItem key={index} value={item.id}>
                  {multiple && <Checkbox checked={value?.includes(item.id)} />}
                  {item.label}
                </MenuItem>
              );
            }
          })
        ) : (
          <></>
        )}
      </Select>
      {!hideHelperText && error !== '' && error !== undefined && (
        <FormHelperText
          sx={{
            color: '#AC3737',
            marginTop: 0,
            fontFamily: 'Instrument Sans',
            fontSize: '0.875rem',
            lineHeight: '1rem',
            height: '1.5rem',
          }}
        >
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default BkSelect;
